<template>
  <div ref="root" class="dr-carousel" :class="centered && 'dr-carousel--centered'">
    <div class="dr-carousel-track" data-carousel-track>
      <div
        v-for="slide in slides"
        :key="slide.key"
        class="dr-carousel-slide"
        data-carousel-slide
        :inert="slide.clone || undefined"
        :aria-hidden="slide.clone ? 'true' : undefined"
      >
        <slot :item="slide.item" :clone="slide.clone" />
      </div>
    </div>
    <slot
      name="controls"
      :prev="prev"
      :next="next"
      :go-to="goTo"
      :current="current"
      :count="items.length"
    />
  </div>
</template>

<script setup lang="ts" generic="T">
/**
 * A Swiper-like strip on utils/carousel for Vue components: one slide per item, the item in
 * the default slot, arrows or dots in `controls`. Slides per view and the gap are the
 * root's custom properties, set through `class` (`[--spv:1.2] md:[--spv:2.5] [--gap:16px]`).
 * A loop renders hidden, inert copies either side, as Swiper's loop did.
 */
import '../../assets/styles/carousel.css'
import { computed, ref } from 'vue'
import { useCarousel } from '../../composables/useCarousel'
import { loopCopies } from '../../utils/carousel'

const props = withDefaults(
  defineProps<{ items: T[]; loop?: boolean; centered?: boolean; speed?: number }>(),
  { loop: false, centered: false, speed: undefined },
)

const slides = computed(() => loopCopies(props.items, props.loop))
const looped = props.loop && props.items.length > 1
// The first real slide: the middle copy's, in a loop.
const first = looped ? props.items.length : 0

const root = ref<HTMLElement>()
const { index, prev, next, slideTo } = useCarousel(root, {
  index: first,
  loopPeriod: looped ? props.items.length : undefined,
  speed: props.speed,
})

/** The real item showing, whichever copy it is. */
const current = computed(() => index.value % Math.max(props.items.length, 1))
const goTo = (item: number, speed?: number) => slideTo(first + item, speed)
</script>
